import { useNavigate } from "@tanstack/react-router"
import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface Campaign {
  id: string
  name: string
  description?: string
  status: "draft" | "active" | "paused" | "completed"
  campaignContext?: string
  objectives?: string[]
  stages?: { id: string; name: string }[]
  conversations?: { id: string }[]
  createdAt: string
  updatedAt: string
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors = {
    draft: "bg-foregroundx text-gray-800  dark:text-gray-300",
    active: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-300",
    paused: "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-300",
    completed: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-300",
  }

  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status as keyof typeof colors] || colors.draft}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

const CampaignsList: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [showCreateModal, setShowCreateModal] = useState(false)

  // Query campaigns with filters
  const {
    data: campaignsData,
    isLoading,
    refetch,
  } = trpc.crm.campaigns.list.useQuery({
    search: searchTerm || undefined,
    status: statusFilter ? (statusFilter as "draft" | "active" | "completed" | "paused") : undefined,
    limit: 50,
    offset: 0,
  })

  // Create campaign mutation
  const createCampaignMutation = trpc.crm.campaigns.create.useMutation({
    onSuccess: (campaign) => {
      setShowCreateModal(false)
      void refetch()
      navigate({ to: `/crm/campaigns/${campaign.id}` })
    },
  })

  // Update campaign status mutation
  const updateCampaignMutation = trpc.crm.campaigns.update.useMutation({
    onSuccess: () => {
      void refetch()
    },
  })

  // Clone campaign mutation
  const cloneCampaignMutation = trpc.crm.campaigns.clone.useMutation({
    onSuccess: (campaign) => {
      void refetch()
      navigate({ to: `/crm/campaigns/${campaign.id}` })
    },
  })

  const handleCreateCampaign = (formData: FormData) => {
    const name = formData.get("name") as string
    const description = formData.get("description") as string
    const campaignContext = formData.get("campaignContext") as string

    if (!name) return

    createCampaignMutation.mutate({
      name,
      description: description || undefined,
      campaignContext: campaignContext || undefined,
      objectives: [],
      successCriteria: [],
      settings: {
        aiModel: "gpt-4", // eslint-disable-line @aix/no-hardcoded-model-strings -- legacy model, no enum available
        maxConversationsPerDay: 100,
        workingHours: {
          start: "09:00",
          end: "18:00",
          timezone: "America/Los_Angeles",
        },
      },
    })
  }

  const handleStatusChange = (campaignId: string, newStatus: "draft" | "active" | "paused" | "completed") => {
    updateCampaignMutation.mutate({
      id: campaignId,
      data: { status: newStatus },
    })
  }

  const handleClone = (campaignId: string, originalName: string) => {
    const newName = prompt("Enter name for cloned campaign:", `${originalName} (Copy)`)
    if (newName) {
      cloneCampaignMutation.mutate({
        campaignId,
        name: newName,
      })
    }
  }

  return (
    <div className="h-full overflow-auto bg-foregroundx ">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold  dark:text-white">Campaigns</h1>
              <p className="mt-2  dark:text-gray-400">Manage your negotiation campaigns</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <span>+</span>
              Create Campaign
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4">
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        {/* Campaigns List */}
        <div className="bg-background  rounded-lg shadow-sm border border-border dark:border-gray-700">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="text-gray-500">Loading campaigns...</div>
            </div>
          ) : campaignsData?.campaigns && campaignsData.campaigns.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-foregroundx dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Context
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Conversations
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {campaignsData.campaigns.map((campaign) => (
                    <tr key={campaign.id} className=" dark:hover:bg-gray-700">
                      <td className="px-6 py-4">
                        <div>
                          <button
                            onClick={() => navigate({ to: `/crm/campaigns/${campaign.id}` })}
                            className="font-medium  dark:text-blue-400 hover:underline"
                          >
                            {campaign.name}
                          </button>
                          {campaign.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{campaign.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm  dark:text-white">
                        {campaign.campaignContext || campaign.description || "No context"}
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={campaign.status} />
                      </td>
                      <td className="px-6 py-4 text-sm  dark:text-white">0</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {new Date(campaign.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {campaign.status === "draft" && (
                            <button
                              onClick={() => handleStatusChange(campaign.id, "active")}
                              className="text-green-600 hover:text-green-800 text-sm"
                            >
                              Activate
                            </button>
                          )}
                          {campaign.status === "active" && (
                            <button
                              onClick={() => handleStatusChange(campaign.id, "paused")}
                              className="text-yellow-600 hover:text-yellow-800 text-sm"
                            >
                              Pause
                            </button>
                          )}
                          {campaign.status === "paused" && (
                            <button
                              onClick={() => handleStatusChange(campaign.id, "active")}
                              className="text-green-600 hover:text-green-800 text-sm"
                            >
                              Resume
                            </button>
                          )}
                          <button
                            onClick={() => handleClone(campaign.id, campaign.name)}
                            className=" hover:text-blue-800 text-sm"
                          >
                            Clone
                          </button>
                          <button
                            onClick={() => navigate({ to: `/crm/campaigns/${campaign.id}` })}
                            className=" hover:text-gray-800 text-sm"
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="text-gray-500 mb-4">No campaigns found</div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Your First Campaign
              </button>
            </div>
          )}
        </div>

        {/* Create Campaign Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background  rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-semibold  dark:text-white mb-4">Create New Campaign</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleCreateCampaign(new FormData(e.currentTarget))
                }}
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      Campaign Name
                    </label>
                    <input
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="Enter campaign name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      name="description"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="Enter campaign description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      Campaign Context (Optional)
                    </label>
                    <textarea
                      name="campaignContext"
                      rows={3}
                      placeholder="e.g., We're hiring cleaners for $25/hr in SF"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Provide context about this campaign to help the AI understand the objective
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2  dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createCampaignMutation.isLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createCampaignMutation.isLoading ? "Creating..." : "Create Campaign"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CampaignsList
